import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { GET } from "../../services/axios.service";
import { OrderProps } from "../../types/order";
import { PartyProps } from "../../types/party";

export interface OpenOrderState {
  orders: OrderProps[];
  totalOrders: number;
  parties: PartyProps[];
  jobworkParties: PartyProps[];
  selectedParty: number;
  selectedJobworkParty: number;
  selectedOrder: number | null;
  loading: boolean;
  error: string | null;
}

const initialState: OpenOrderState = {
  orders: [],
  totalOrders: 0,
  parties: [],
  jobworkParties: [],
  selectedParty: 0,
  selectedJobworkParty: 0,
  selectedOrder: null,
  loading: false,
  error: null,
};

export const fetchOpenOrdersData = createAsyncThunk(
  "fetchOpenOrdersData",
  async (req: { page: number; party_id?: number; jobwork_party_id?: number; order_id?: number }) => {
    let response: any;
    await GET(`/v1/worker/orders`, { ...req, status: 0 })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchPartiesData = createAsyncThunk(
  "fetchOpenOrdersPartiesData",
  async () => {
    let response: any;
    await GET(`/v1/worker/parties`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchJobworkPartiesData = createAsyncThunk(
  "fetchOpenOrdersJobworkPartiesData",
  async () => {
    let response: any;
    await GET(`/v1/worker/jobwork-parties`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const openOrderSliceSlice = createSlice({
  name: "openOrder",
  initialState,
  reducers: {
    updateSelectedParty: (state, action: PayloadAction<number>) => {
      state.selectedParty = action.payload;
    },
    updateSelectedJobworkParty: (state, action: PayloadAction<number>) => {
      state.selectedJobworkParty = action.payload;
    },
    updateSelectedOrder: (state, action: PayloadAction<number | null>) => {
      state.selectedOrder = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchOpenOrdersData.pending, (state, action: any) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchOpenOrdersData.fulfilled, (state, action: any) => {
      state.loading = false;
      if (action.payload.data.status === "success") {
        if (
          action.payload.data.data.records.length > 0 &&
          action.payload.data.data.page > 1
        )
          state.orders = [...state.orders, ...action.payload.data.data.records];
        else state.orders = action.payload.data.data.records;
        state.totalOrders = action.payload.data.data.total_records;
        state.error = null;
      } else {
        state.orders = [];
        state.error =
          action?.payload?.data?.message ||
          "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";
      }
    });
    builder.addCase(fetchOpenOrdersData.rejected, (state, action: any) => {
      state.loading = false;
      state.error =
        action?.payload?.data?.message ||
        "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";
    });
    builder.addCase(fetchPartiesData.pending, (state, action: any) => {
      state.parties = [];
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchPartiesData.fulfilled, (state, action: any) => {
      state.loading = false;
      if (action.payload.data.status === "success") {
        state.parties = action.payload.data.data;
        state.error = null;
      } else {
        state.parties = [];
        state.error =
          action?.payload?.data?.message ||
          "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";
      }
    });
    builder.addCase(fetchPartiesData.rejected, (state, action: any) => {
      state.parties = [];
      state.loading = false;
      state.error =
        action?.payload?.data?.message ||
        "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";
    });
    builder.addCase(fetchJobworkPartiesData.pending, (state, action: any) => {
      state.jobworkParties = [];
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchJobworkPartiesData.fulfilled, (state, action: any) => {
      state.loading = false;
      if (action.payload.data.status === "success") {
        state.jobworkParties = action.payload.data.data;
        state.error = null;
      } else {
        state.jobworkParties = [];
        state.error =
          action?.payload?.data?.message ||
          "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";
      }
    });
    builder.addCase(fetchJobworkPartiesData.rejected, (state, action: any) => {
      state.jobworkParties = [];
      state.loading = false;
      state.error =
        action?.payload?.data?.message ||
        "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";
    });
  },
});

export const { updateSelectedParty, updateSelectedJobworkParty, updateSelectedOrder } =
  openOrderSliceSlice.actions;

export const getOrders = (state: RootState) => state.openOrder.orders;

export const getTotalOrders = (state: RootState) => state.openOrder.totalOrders;

export const getParties = (state: RootState) => state.openOrder.parties;

export const getJobworkParties = (state: RootState) => state.openOrder.jobworkParties;

export const getSelectedParty = (state: RootState) =>
  state.openOrder.selectedParty;

export const getSelectedJobworkParty = (state: RootState) =>
  state.openOrder.selectedJobworkParty;

export const getSelectedOrder = (state: RootState) =>
  state.openOrder.selectedOrder;

export default openOrderSliceSlice.reducer;
